import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import { config } from "../../../config/envs";
import { generateTraceId } from "../../helpers";
import { parseBrPhone } from "../../helpers/parseBrPhone";
import useFeatureFlag from "../../hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "../../constants/FeatureFlag";
import { CheckoutAPI } from "../../api/payments/checkout/api";
import { CourtesySubscriptionsAPI } from "../../api/payments/courtesySubscriptions/api";
import { toCourtesyJusSubInterval } from "../../constants/courtesy";

const toCourtesyDays = (durationType, duration) => {
  const value = Number(duration);
  if (!duration || !Number.isInteger(value) || value <= 0) {
    return undefined;
  }
  return durationType === "activeMonths" ? value * 30 : value;
};

// O checkout do jussub não devolve um "expira em X segundos" explícito, só a
// data de vencimento (dueAt). Usamos uma janela fixa razoável para o timer
// visual do modal (mesma ordem de grandeza do pix legado via jusbill).
const DEFAULT_PIX_TIMER_SECONDS = 3600;

const buildJusSubAddress = values => ({
  street: values.address,
  streetNumber: values.address_number,
  neighborhood: values.district,
  zipcode: values.postal_code,
  city: values.city?.name,
  state: values.state?.state,
  country: "BR",
  complement: values.address_complement || undefined
});

export function useAdminClientesSubmit() {
  const canAccess = useFeatureFlag(FEATURE_FLAGS.KILL_SWITCH.ADMIN_PAYMENTS_ARCH_V2);
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const [pixModalData, setPixModalData] = useState(null);

  const submitCourtesyJusbill = async (values, { setSubmitting, resetForm }) => {
    const payload = {
      provider: "jusbill",
      ReleaseCategory: values.courtesy_product,
      transactionId: uuidv4(),
      planId: values.plan?.id != null ? String(values.plan.id) : undefined,
      user: {
        email: values.email,
        name: values.name,
        documentNumber: values.document_number,
        phone: values.phone,
        type: values.type,
        client_type: values.client_type,
        profession: values.profession,
        oab: values.oab,
        oab_state: values.oab_state,
        address: buildJusSubAddress(values)
      }
    };

    const duration = Number(values.courtesy_duration);
    if (values.courtesy_duration && Number.isInteger(duration) && duration > 0) {
      payload[values.courtesy_duration_type] = duration;
    }

    try {
      const response = await axios.post(
        config.app.adminBaseUrl + "/courtesy-subscriptions/",
        payload,
        { headers: { "x-trace-id": generateTraceId() } }
      );

      if (![200, 201, 204].includes(response.status)) {
        throw new Error("Erro ao criar cortesia.");
      }

      toast.success("Cortesia criada com sucesso!");
      resetForm();
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          "Ocorreu um erro ao criar a cortesia, por favor, tente novamente."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitCourtesyJusSub = async (values, { setSubmitting, resetForm }) => {
    const { ddd, number } = parseBrPhone(values.phone);

    const payload = {
      provider: "jussub",
      customer: {
        email: values.email,
        name: values.name,
        documentNumber: values.document_number,
        phone: `(${ddd}) ${number}`,
        address: buildJusSubAddress(values)
      },
      planId: values.plan?.id != null ? String(values.plan.id) : undefined,
      courtesyDays: toCourtesyJusSubInterval(
        toCourtesyDays(values.courtesy_duration_type, values.courtesy_duration)
      ),
      courtesyReason: values.courtesy_product
    };

    try {
      await CourtesySubscriptionsAPI.createForNewClient(payload);
      toast.success("Cortesia criada com sucesso!");
      resetForm();
    } catch (err) {
      // erro já exibido via toast pela camada de API
    } finally {
      setSubmitting(false);
    }
  };

  const submitPaidJusbill = (values, { setSubmitting, resetForm }) => {
    dispatch({
      type: "ADMIN_SAVE_USER",
      payload: {
        submitValues: { ...values, provider: values.provider.id },
        setSubmitting,
        adminUser: user,
        callback: () => {
          resetForm();
        }
      }
    });
  };

  const submitPaidJusSub = async (values, { setSubmitting, resetForm }) => {
    const { ddd, number } = parseBrPhone(values.phone);

    const payload = {
      paymentMethod: "PIX",
      customer: {
        email: values.email,
        name: values.name,
        documentNumber: (values.document_number || "").replace(/\D/g, ""),
        address: buildJusSubAddress(values),
        phone: {
          countryCode: "55",
          ddd,
          number
        }
      },
      items: [
        {
          itemId: values.plan?.id != null ? String(values.plan.id) : undefined,
          itemType: "PLAN"
        }
      ],
      createdByEmail: user?.email
    };

    try {
      const response = await CheckoutAPI.createWithPlan(payload);
      const oneTimePayment = response.data?.oneTimePayment;

      toast.success("Cadastro realizado com sucesso!.");

      if (oneTimePayment) {
        setPixModalData({
          pixCode: oneTimePayment.qrCode,
          pixQR: oneTimePayment.qrCodeUrl || oneTimePayment.url,
          pixTimer: DEFAULT_PIX_TIMER_SECONDS
        });
      }

      resetForm();
    } catch (err) {
      // erro já exibido via toast pela camada de API
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (values, helpers) => {
    if (values.is_courtesy) {
      return canAccess.isFeatureFlagEnable
        ? submitCourtesyJusSub(values, helpers)
        : submitCourtesyJusbill(values, helpers);
    }

    return canAccess.isFeatureFlagEnable
      ? submitPaidJusSub(values, helpers)
      : submitPaidJusbill(values, helpers);
  };

  return {
    onSubmit,
    useJusSub: canAccess.isFeatureFlagEnable,
    flagResolved: canAccess.flagResolved,
    pixModalData,
    closePixModal: () => setPixModalData(null)
  };
}
